import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { parseApiDate } from '../../utils/date.utils';

type DateValue = Date | string | null;

/**
 * CVA-компонент выбора даты (MatDatepicker) с единой `FormControl<Date | null>`.
 *
 * Принимает `Date | string | null` (для удобства миграции со строк API —
 * строки вида `YYYY-MM-DD` / `dd.MM.yyyy` парсятся автоматически).
 * Отдаёт `Date | null` через `onChange`.
 *
 * Ошибки валидации читаются с родительского `NgControl` (`required`,
 * `dateRange`, `min`, `max`, `serverError`) и показываются под полем.
 */
@Component({
  selector: 'app-date-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
  ],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DateInputComponent),
    },
  ],
})
export class DateInputComponent implements ControlValueAccessor, OnInit {
  // ── Лейбл (требование пользователя) ────────────────────────────────
  @Input() label = 'Дата';

  // ── Стандартные CVA-inputs ─────────────────────────────────────────
  @Input() required = false;

  // ── Опционально ────────────────────────────────────────────────────
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Input() placeholder = '';
  @Input() errorMessage = 'Укажите дату';
  @Input() dateRangeErrorMessage = 'Дата окончания должна быть не раньше даты начала';
  @Input() minErrorMessage = 'Дата вне допустимого диапазона';
  @Input() maxErrorMessage = 'Дата вне допустимого диапазона';

  // ── Внутренний контрол (без валидаторов — они на родителе) ────────
  readonly dateControl = new FormControl<Date | null>(null);

  // `onTouched` используется в шаблоне `(blur)="onTouched()"` — Angular
  // strict-templates требует, чтобы такие свойства были не `private`.
  // Саму функцию перезаписывает Angular Forms через `registerOnTouched`.
  onChange: (v: Date | null) => void = () => {};
  onTouched: () => void = () => {};

  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;

  private get parentErrors(): Record<string, any> | null {
    const control = this.ngControl?.control as FormControl | null;
    return control?.errors ?? null;
  }

  ngOnInit(): void {
    // Отложенное получение NgControl — разрывает цикл DI.
    // К моменту ngOnInit компонент уже создан, и NgControl уже привязан
    // к этому компоненту через NG_VALUE_ACCESSOR-провайдер.
    // Нам NgControl нужен только для чтения parentErrors (для отображения ошибок валидации).
    try {
      this.ngControl = this.injector.get(NgControl, null, { self: true });
    } catch {
      this.ngControl = null;
    }
  }

  constructor() {
    // Любое изменение — пушим актуальный Date|null наверх.
    this.dateControl.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });
  }

  // ── CVA-методы ─────────────────────────────────────────────────────
  writeValue(value: DateValue): void {
    const parsed = parseApiDate(value);
    this.dateControl.patchValue(parsed, { emitEvent: false });
    // Ручной push — родитель ждёт актуальное значение после writeValue
    // (и в случае, если значение не изменилось — чтобы valueChanges в родителе
    // синхронизировался с реальным состоянием контрола).
    this.onChange(parsed);
  }

  registerOnChange(fn: (v: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable({ emitEvent: false });
    } else {
      this.dateControl.enable({ emitEvent: false });
    }
  }

  // ── View helpers (для шаблона) ─────────────────────────────────────
  showError(): boolean {
    const errors = this.parentErrors;
    if (!errors) return false;
    return !!(
      errors['required'] ||
      errors['dateRange'] ||
      errors['min'] ||
      errors['max'] ||
      errors['serverError']
    );
  }

  currentErrorMessage(): string {
    const errors = this.parentErrors;
    if (!errors) return '';
    if (errors['required']) return this.errorMessage;
    if (errors['dateRange']) return this.dateRangeErrorMessage;
    if (errors['min']) return this.minErrorMessage;
    if (errors['max']) return this.maxErrorMessage;
    if (errors['serverError']) return String(errors['serverError']);
    return '';
  }
}
